import { useEffect, useMemo, useState } from "react";
import {
    FiCreditCard,
    FiPlusCircle,
    FiRepeat,
    FiCheckCircle,
    FiSlash,
    FiGrid,
} from "react-icons/fi";
import AppLayout from "../components/common/AppLayout";
import ConfirmModal from "../components/common/ConfirmModal";
import CuentaCard from "../components/cuentas/CuentaCard";
import CuentaForm from "../components/cuentas/CuentaForm";
import TransferenciaForm from "../components/cuentas/TransferenciaForm";
import useCuentas from "../hooks/useCuentas";
import { formatCurrency } from "../utils/formatCurrency";
import { apiFetch } from "../api/apiClient";

export default function CuentasPage() {
    const {
        cuentas,
        loading,
        cuentaSeleccionada,
        movimientosCuenta,
        guardarCuenta,
        inactivar,
        activar,
        verDetalleCuenta,
        transferir,
    } = useCuentas();

    const [openCuentaForm, setOpenCuentaForm] = useState(false);
    const [openTransferencia, setOpenTransferencia] = useState(false);
    const [editing, setEditing] = useState(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [cuentaAInactivar, setCuentaAInactivar] = useState(null);

    const [tiposCuenta, setTiposCuenta] = useState([]);
    const [bancos, setBancos] = useState([]);
    const [monedas, setMonedas] = useState([]);

    const [tabEstado, setTabEstado] = useState("ACTIVAS");

    useEffect(() => {
        async function cargarCatalogos() {
            try {
                const [tipos, bancosResp, monedasResp] = await Promise.all([
                    apiFetch("/api/catalogos/tipos-cuenta"),
                    apiFetch("/api/catalogos/bancos"),
                    apiFetch("/api/catalogos/monedas"),
                ]);

                setTiposCuenta(Array.isArray(tipos) ? tipos : []);
                setBancos(Array.isArray(bancosResp) ? bancosResp : []);
                setMonedas(Array.isArray(monedasResp) ? monedasResp : []);
            } catch (e) {
                console.error("Error cargando catálogos", e);
            }
        }

        cargarCatalogos();
    }, []);

    const cuentasFiltradas = useMemo(() => {
        if (tabEstado === "TODAS") return cuentas;

        if (tabEstado === "ACTIVAS") {
            return cuentas.filter((c) => String(c.estado) === "1");
        }

        if (tabEstado === "INACTIVAS") {
            return cuentas.filter((c) => String(c.estado) !== "1");
        }

        return cuentas;
    }, [cuentas, tabEstado]);

    return (
        <AppLayout
            title={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <FiCreditCard size={30} />
                    Cuentas
                </span>
            }
        >
            <div className="toolbar-row">
                <button
                    className="btn-primary"
                    type="button"
                    onClick={() => {
                        setEditing(null);
                        setOpenCuentaForm(true);
                    }}
                >
                    <FiPlusCircle size={18} />
                    Nueva cuenta
                </button>

                <button
                    className="btn-info"
                    type="button"
                    onClick={() => setOpenTransferencia(true)}
                >
                    <FiRepeat size={18} />
                    Nueva transferencia
                </button>
            </div>

            <div
                className="card-panel"
                style={{
                    marginBottom: 18,
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    alignItems: "center",
                }}
            >
                <button
                    type="button"
                    className={tabEstado === "ACTIVAS" ? "btn-primary" : "btn-secondary"}
                    onClick={() => setTabEstado("ACTIVAS")}
                >
                    <FiCheckCircle size={16} />
                    Activas
                </button>

                <button
                    type="button"
                    className={tabEstado === "INACTIVAS" ? "btn-primary" : "btn-secondary"}
                    onClick={() => setTabEstado("INACTIVAS")}
                >
                    <FiSlash size={16} />
                    Inactivas
                </button>

                <button
                    type="button"
                    className={tabEstado === "TODAS" ? "btn-primary" : "btn-secondary"}
                    onClick={() => setTabEstado("TODAS")}
                >
                    <FiGrid size={16} />
                    Todas
                </button>
            </div>

            {loading ? (
                <p>Cargando cuentas...</p>
            ) : cuentasFiltradas.length === 0 ? (
                <div className="card-panel empty-state">
                    No hay cuentas para la pestaña seleccionada.
                </div>
            ) : (
                <div className="cuentas-grid">
                    {cuentasFiltradas.map((cuenta) => (
                        <CuentaCard
                            key={cuenta.idCuenta || cuenta.id_cuenta}
                            cuenta={cuenta}
                            onClick={verDetalleCuenta}
                            onEdit={(row) => {
                                setEditing(row);
                                setOpenCuentaForm(true);
                            }}
                            onInactivar={(row) => {
                                setCuentaAInactivar(row);
                                setConfirmOpen(true);
                            }}
                            onActivar={async (row) => {
                                const idCuenta = row.idCuenta ?? row.id_cuenta;
                                await activar(idCuenta);
                            }}
                        />
                    ))}
                </div>
            )}

            {cuentaSeleccionada && (
                <div className="card-panel" style={{ marginTop: 20 }}>
                    <h3>Detalle de cuenta: {cuentaSeleccionada.nombre}</h3>
                    <p>
                        <strong>Saldo actual:</strong>{" "}
                        {formatCurrency(
                            cuentaSeleccionada.saldoActual || cuentaSeleccionada.saldo_actual || 0,
                            cuentaSeleccionada.monedaCodigo || "PEN"
                        )}
                    </p>

                    <div className="table-wrap">
                        <table className="table-app">
                            <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Tipo</th>
                                <th>Descripción</th>
                                <th>Monto</th>
                                <th>Estado</th>
                            </tr>
                            </thead>
                            <tbody>
                            {movimientosCuenta.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="empty-state">
                                        No hay movimientos para esta cuenta
                                    </td>
                                </tr>
                            ) : (
                                movimientosCuenta.map((row) => (
                                    <tr key={row.idMovimiento || row.id_movimiento}>
                                        <td>{row.fechaMovimiento || row.fecha_movimiento}</td>
                                        <td>{row.tipoMovimiento || row.tipo_movimiento}</td>
                                        <td>{row.descripcion}</td>
                                        <td>
                                            {formatCurrency(
                                                row.monto || 0,
                                                cuentaSeleccionada.monedaCodigo || "PEN"
                                            )}
                                        </td>
                                        <td>{row.estado}</td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {openCuentaForm && (
                <CuentaForm
                    initialData={editing}
                    tiposCuenta={tiposCuenta}
                    bancos={bancos}
                    monedas={monedas}
                    onClose={() => setOpenCuentaForm(false)}
                    onSave={async (payload) => {
                        await guardarCuenta(payload, editing?.idCuenta || editing?.id_cuenta);
                        setOpenCuentaForm(false);
                    }}
                />
            )}

            {openTransferencia && (
                <TransferenciaForm
                    cuentas={cuentas}
                    monedas={monedas}
                    onClose={() => setOpenTransferencia(false)}
                    onSave={async (payload) => {
                        await transferir(payload);
                        setOpenTransferencia(false);
                    }}
                />
            )}

            <ConfirmModal
                open={confirmOpen}
                title="Inactivar cuenta"
                message={`¿Seguro que deseas inactivar la cuenta "${cuentaAInactivar?.nombre || ""}"? Esta acción puede afectar su uso en operaciones futuras.`}
                confirmText="Sí, inactivar"
                cancelText="Cancelar"
                variant="danger"
                onClose={() => {
                    setConfirmOpen(false);
                    setCuentaAInactivar(null);
                }}
                onConfirm={async () => {
                    const idCuenta =
                        cuentaAInactivar?.idCuenta ?? cuentaAInactivar?.id_cuenta;
                    await inactivar(idCuenta);
                    setConfirmOpen(false);
                    setCuentaAInactivar(null);
                }}
            />
        </AppLayout>
    );
}